import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DsColors, DsPageMessageVariants, Icons, IconScale, IconStyles } from '@levelaccess/design-system';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { NgbPaginationConfig } from '@ng-bootstrap/ng-bootstrap';
import { CsqLogoService } from '../../../../../components/shared/csq-logo/csq-logo.service';
import { ContentsquareIntegrationService } from '../../../../../services/contentsquare-integration.service';
import { Api } from '../../../../../../../shared/constants/api';
import {
  IRuleComponentMetrics,
  IRuleSummary,
  IScanSummary,
  IScanSummaryAllItem,
} from '../../../../../../../shared/interfaces/scan-summaries.interface';
import { $ruleSummary, $scanSummaries } from '../../../../../../../shared/constants/scan-summaries';
import {
  ITableColumn,
  ITableConfig,
  ITableDsPageMessageConfig,
  ITableEmptyState,
  ITableRow,
  SortEvent,
  TableEmptyStateType,
} from '../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { DEFAULT_ENTRY_LIMITS } from '../../../../../constants/form.constants';
import { TranslateService } from '../../../../../translate/translate.service';
import { NgbTableUtilities } from '../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { IScanAudit } from '../../../../../../../shared/interfaces/scan-audit.interface';
import { MatchComponentService } from '../../../../../services/match-component.service';
import {
  IMatchComponentsResponse,
  IMatchComponentWithImage,
} from '../../../../../../../shared/interfaces/match-component.interface';
import {
  $matchComponent,
  $matchComponentEngagementMetrics,
  MatchComponentStatus,
} from '../../../../../../../shared/constants/match-component';
import { AuditTypes } from '../../../../../../../shared/constants/audit-types';
import { IAuditRule } from '../../../../../../../shared/interfaces/audit-rule.interface';
import { EmptyObject } from '../../../../../../../shared/interfaces/empty-object.interface';
import { SharedAuditsUtility } from '../../../../../../../shared/utils/audits.utility';
import { $auditRule } from '../../../../../../../shared/constants/audit-rule';
import { $scanAudit } from '../../../../../../../shared/constants/scan-audit';
import {
  $auditTool,
  AccessibilityAuditToolNames,
  AuditToolNameToAuditType,
} from '../../../../../../../shared/constants/audit-tool';
import { $severity } from '../../../../../../../shared/constants/accessibility';
import { $sortingOrder } from '../../../../../../../shared/constants/sort';
import { IThumbnailCellConfig } from '../../../../../components/table/ngb-table/cells/thumbnail-cell/thumbnail-cell.component';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';

type NoMatchingResults = EmptyObject;

interface IComponentIssueCount extends IRuleComponentMetrics {
  failures: number;
  reviews: number;
  byRules: { [rule_id: string]: IRuleComponentMetrics };
  criticalFindings: number;
  totalFindings: number;
}

interface IByComponentIssueCount {
  [component_id: string]: IComponentIssueCount;
}

enum ComponentsListTableColumns {
  name = 'name',
  open = 'open',
  image = 'image',

  dismissed = 'dismissed',
  failures = 'failures',
  reviews = 'reviews',

  criticalFindings = 'criticalFindings',
  totalFindings = 'totalFindings',
  clickCount = 'clickCount',
  clickRate = 'clickRate',
}

@Component({
  selector: 'app-view-scan-results-components',
  templateUrl: './view-scan-results-components.component.html',
  styleUrls: ['./view-scan-results-components.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NgbPaginationConfig],
  standalone: false,
})
export class ViewScanResultsComponentsComponent implements OnInit, OnDestroy {
  private readonly scanSummary$: ReplaySubject<IScanSummaryAllItem | NoMatchingResults>;
  private readonly subscriptions: Subscription;
  private readonly digitalPropertyId$: ReplaySubject<string>;
  private readonly workspaceId$: ReplaySubject<string>;
  private readonly components$: ReplaySubject<Map<string, IMatchComponentWithImage>>;
  private readonly scanAudits$: ReplaySubject<IScanAudit | NoMatchingResults>;
  private readonly auditTool$: ReplaySubject<AccessibilityAuditToolNames>;
  private contentsquareIntegrationEnabled: boolean = false;
  private useDsPageMessage: boolean;
  public pageSizes: readonly number[];

  public readonly issuesByComponents$: ReplaySubject<IByComponentIssueCount>;

  @Input()
  public set scanSummary(value: IScanSummaryAllItem | NoMatchingResults) {
    this.scanSummary$.next(value);
  }

  @Input()
  public set digitalPropertyId(value: string) {
    this.digitalPropertyId$.next(value);
  }

  @Input()
  public set workspaceId(value: string) {
    this.workspaceId$.next(value);
  }

  @Input()
  public set auditTool(value: AccessibilityAuditToolNames) {
    this.auditTool$.next(value);
  }

  @Input()
  public set scanAudit(value: IScanAudit | NoMatchingResults) {
    this.scanAudits$.next(value);
  }

  @Input()
  public showImageCell: boolean;
  @Input()
  public showListOfComponentsLabel: boolean;
  @Input()
  public basePath: string;

  public readonly page$: BehaviorSubject<number>;
  public readonly pageSize$: BehaviorSubject<number>;
  public readonly totalRows$: BehaviorSubject<number>;
  public readonly sortOption$: BehaviorSubject<SortEvent>;
  public readonly Api: typeof Api;
  public readonly tableData$: Subject<ITableRow[]>;

  constructor(
    private translateService: TranslateService,
    private matchComponentService: MatchComponentService,
    private csqLogoService: CsqLogoService,
    private contentsquareIntegrationService: ContentsquareIntegrationService,
    private viewContainerRef: ViewContainerRef,
    private featureFlagService: FeatureFlagService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.showImageCell = false;
    this.showListOfComponentsLabel = false;
    this.pageSizes = DEFAULT_ENTRY_LIMITS;
    this.page$ = new BehaviorSubject<number>(1);
    this.pageSize$ = new BehaviorSubject<number>(this.pageSizes[1]);
    this.sortOption$ = new BehaviorSubject<SortEvent>({
      column: ComponentsListTableColumns.open,
      direction: $sortingOrder.desc,
    });
    this.totalRows$ = new BehaviorSubject<number>(0);
    this.issuesByComponents$ = new ReplaySubject<IByComponentIssueCount>(1);
    this.tableData$ = new BehaviorSubject<ITableRow[]>(null);
    this.digitalPropertyId$ = new ReplaySubject<string>(1);
    this.workspaceId$ = new ReplaySubject<string>(1);
    this.components$ = new ReplaySubject<Map<string, IMatchComponentWithImage>>(1);
    this.scanSummary$ = new ReplaySubject<IScanSummaryAllItem>(1);
    this.scanAudits$ = new ReplaySubject<IScanAudit>(1);
    this.auditTool$ = new ReplaySubject<AccessibilityAuditToolNames>(1);
    this.subscriptions = new Subscription();
    this.useDsPageMessage = this.featureFlagService.variation(FeatureFlagCollection.dsPageMessage, false);
    this.Api = Api;
  }

  private groupIssuesByComponents([scanSummary, scanAudits]: [
    IScanSummary | NoMatchingResults,
    IScanAudit | NoMatchingResults,
  ]): IByComponentIssueCount {
    // This occurs when the default testing tool is not included in scanSummaries
    if (Object.keys(scanSummary).length === 0) {
      // We want to return no components
      return {};
    }

    const byComponentIssues: IByComponentIssueCount = {};
    const auditType: AuditTypes = AuditToolNameToAuditType[scanSummary[$scanSummaries.toolId][$auditTool.name]];
    const getRuleDefinition = (ruleName: string): IAuditRule => {
      return scanAudits[$scanAudit.audits][auditType].rules[ruleName];
    };

    const isWcagRule = ([ruleId, ruleSummary]: [string, IRuleSummary]): boolean => {
      const rule: IAuditRule = getRuleDefinition(ruleSummary[$ruleSummary.ruleName]);
      return SharedAuditsUtility.hasWcag(rule[$auditRule.standards]);
    };

    const addComponentMetric = (
      ruleId: string,
      ruleSeverity: $severity,
      [componentId, metrics]: [string, IRuleComponentMetrics],
    ): void => {
      const componentCount: IComponentIssueCount = byComponentIssues[componentId];
      const isInfo = ruleSeverity === $severity.info;
      const findingsCount = metrics[$ruleSummary.errors];

      if (componentCount) {
        componentCount[$ruleSummary.errors] += metrics[$ruleSummary.errors];
        componentCount[$ruleSummary.ignored] = (componentCount[$ruleSummary.ignored] ?? 0) + (metrics[$ruleSummary.ignored] ?? 0);
        if (isInfo) {
          componentCount.reviews += metrics[$ruleSummary.errors];
        } else {
          componentCount.failures += metrics[$ruleSummary.errors];
        }
        componentCount.byRules[ruleId] = { ...metrics };
        componentCount.criticalFindings += ruleSeverity === $severity.critical ? findingsCount : 0;
        componentCount.totalFindings += findingsCount;
      } else {
        byComponentIssues[componentId] = {
          ...metrics,
          failures: isInfo ? 0 : metrics[$ruleSummary.errors],
          reviews: isInfo ? metrics[$ruleSummary.errors] : 0,
          byRules: { [ruleId]: { ...metrics } },
          criticalFindings: ruleSeverity === $severity.critical ? findingsCount : 0,
          totalFindings: findingsCount,
        };
      }
    };

    const addToComponentsIssueCount = ([ruleId, ruleSummary]: [string, IRuleSummary]): void => {
      const rule: IAuditRule = getRuleDefinition(ruleSummary[$ruleSummary.ruleName]);
      const ruleSeverity = rule[$auditRule.severity];
      Object.entries(ruleSummary[$ruleSummary.components]).forEach(addComponentMetric.bind(this, ruleId, ruleSeverity));
    };

    Object.entries(scanSummary[$scanSummaries.metrics] || {})
      .filter(
        ([ruleId, ruleSummary]: [string, IRuleSummary]) =>
          SharedAuditsUtility.isRuleTriggered(ruleSummary) &&
          SharedAuditsUtility.isRuleWithComponentResults(ruleSummary) &&
          isWcagRule([ruleId, ruleSummary]),
      )
      .forEach(addToComponentsIssueCount);

    return byComponentIssues;
  }

  private sortEntries(
    sortOptions: SortEvent,
    components: Map<string, IMatchComponentWithImage>,
    a: [string, IComponentIssueCount],
    b: [string, IComponentIssueCount],
  ): number {
    let ret: number = 0;

    const sortColumn: string = sortOptions.direction === $sortingOrder.all ? ComponentsListTableColumns.open : sortOptions.column;

    const defaultSort: (o1: [string, IComponentIssueCount], o2: [string, IComponentIssueCount]) => number = (
      o1: [string, IComponentIssueCount],
      o2: [string, IComponentIssueCount],
    ) => o1[1][$ruleSummary.errors] - o2[1][$ruleSummary.errors];

    const componentA = components.get(a[0]);
    const componentB = components.get(b[0]);

    switch (sortColumn) {
      case ComponentsListTableColumns.name:
        const aName: string = (components.get(a[0])?.[$matchComponent.name] ?? '').toLowerCase();
        const bName: string = (components.get(b[0])?.[$matchComponent.name] ?? '').toLowerCase();
        ret = aName.localeCompare(bName);
        break;
      case ComponentsListTableColumns.dismissed:
        ret = (a[1][$ruleSummary.ignored] ?? 0) - (b[1][$ruleSummary.ignored] ?? 0);
        break;
      case ComponentsListTableColumns.failures:
        ret = a[1].failures - b[1].failures;
        break;
      case ComponentsListTableColumns.reviews:
        ret = a[1].reviews - b[1].reviews;
        break;
      case ComponentsListTableColumns.totalFindings:
        ret = a[1].totalFindings - b[1].totalFindings;
        break;
      case ComponentsListTableColumns.criticalFindings:
        ret = a[1].criticalFindings - b[1].criticalFindings;
        break;
      case ComponentsListTableColumns.clickCount:
        ret =
          (componentA?.[$matchComponent.engagementMetrics]?.[$matchComponentEngagementMetrics.clickCount] ?? 0) -
          (componentB?.[$matchComponent.engagementMetrics]?.[$matchComponentEngagementMetrics.clickCount] ?? 0);
        break;
      case ComponentsListTableColumns.clickRate:
        ret =
          (componentA?.[$matchComponent.engagementMetrics]?.[$matchComponentEngagementMetrics.clickRate] ?? 0) -
          (componentB?.[$matchComponent.engagementMetrics]?.[$matchComponentEngagementMetrics.clickRate] ?? 0);
        break;
      default:
        // ComponentsListTableColumns.open:
        ret = defaultSort(a, b);
        break;
    }

    if (sortOptions.direction !== $sortingOrder.asc) {
      ret = -ret;
    }

    // if primary sort is equal, use default sort in descending direction as secondary
    if (ret === 0) {
      ret = -defaultSort(a, b);
    }

    return ret;
  }

  private getImageCellAttachmentConfig(matchComponent: IMatchComponentWithImage): IThumbnailCellConfig {
    const thumbnailAlt: string = this.translateService.instant('component_thumbnail', matchComponent.name);

    return {
      imageUrl: matchComponent[$matchComponent.imageUrl],
      thumbnailUrl: matchComponent[$matchComponent.thumbnailUrl],
      thumbnailAlt,
    };
  }

  private getTableData([issuesByComponent, pageNumber, pageSize, components, auditTool, sortOptions]: [
    IByComponentIssueCount,
    number,
    number,
    Map<string, IMatchComponentWithImage>,
    AccessibilityAuditToolNames,
    SortEvent,
  ]): ITableRow[] {
    const auditType: AuditTypes = AuditToolNameToAuditType[auditTool];
    const toRowData = ([componentId, issueCount]: [string, IComponentIssueCount]): ITableRow => {
      const component: IMatchComponentWithImage = components.get(componentId);
      return {
        originalData: [componentId, issueCount],
        data: {
          [ComponentsListTableColumns.name]: NgbTableUtilities.linkCell({
            text: component[$matchComponent.name],
            attributes: {
              routerLink: `${this.basePath}/${Api.component_report}/${auditType}/${componentId}`,
            },
            tagLabel:
              component[$matchComponent.status] === MatchComponentStatus.archived
                ? this.translateService.instant('archived')
                : undefined,
          }),
          [ComponentsListTableColumns.image]: NgbTableUtilities.thumbnailCell(this.getImageCellAttachmentConfig(component)),
          ...(this.contentsquareIntegrationEnabled
            ? {
                [ComponentsListTableColumns.totalFindings]: NgbTableUtilities.textCell({
                  text: String(issueCount.totalFindings),
                }),
                [ComponentsListTableColumns.criticalFindings]: NgbTableUtilities.textCell({
                  text: String(issueCount.criticalFindings),
                }),
                [ComponentsListTableColumns.clickCount]: NgbTableUtilities.textCell({
                  text: String(component[$matchComponent.engagementMetrics]?.[$matchComponentEngagementMetrics.clickCount] ?? 0),
                }),
                [ComponentsListTableColumns.clickRate]: NgbTableUtilities.textCell({
                  text: SharedCommonUtility.formatPercentageString(
                    component[$matchComponent.engagementMetrics]?.[$matchComponentEngagementMetrics.clickRate] ?? 0,
                  ),
                }),
              }
            : {
                [ComponentsListTableColumns.open]: NgbTableUtilities.textCell({ text: String(issueCount[$ruleSummary.errors]) }),
                [ComponentsListTableColumns.dismissed]: NgbTableUtilities.textCell({
                  text: String(issueCount[$ruleSummary.ignored] ?? 0),
                }),
                [ComponentsListTableColumns.failures]: NgbTableUtilities.textCell({ text: String(issueCount.failures) }),
                [ComponentsListTableColumns.reviews]: NgbTableUtilities.textCell({ text: String(issueCount.reviews) }),
              }),
        },
      };
    };

    return Object.entries(issuesByComponent)
      .sort(this.sortEntries.bind(this, sortOptions, components))
      .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
      .map(toRowData);
  }

  private getComponentData([workspaceId, dpId]: [string, string]): Observable<Map<string, IMatchComponentWithImage>> {
    return this.matchComponentService.getAll(workspaceId, dpId).pipe(
      map((components: IMatchComponentsResponse): Map<string, IMatchComponentWithImage> => {
        return new Map<string, IMatchComponentWithImage>(
          components[$matchComponent.components].map((c: IMatchComponentWithImage) => [c[$matchComponent._id], c]),
        );
      }),
    );
  }

  public get tableConfig(): ITableConfig {
    const csqLogoTemplate: TemplateRef<any> = this.csqLogoService.createCsqLogoTemplate(this.viewContainerRef);

    const columns: Record<string, ITableColumn> = {
      [ComponentsListTableColumns.name]: {
        translationKey: 'component_name',
        sortingEnabled: true,
      },
      ...(this.showImageCell
        ? {
            [ComponentsListTableColumns.image]: {
              translationKey: 'match_component_image',
              sortingEnabled: false,
            },
          }
        : {}),
      ...(this.contentsquareIntegrationEnabled
        ? {
            [ComponentsListTableColumns.totalFindings]: {
              translationKey: 'total_findings',
              sortingEnabled: true,
            },
            [ComponentsListTableColumns.criticalFindings]: {
              translationKey: 'critical_findings',
              sortingEnabled: true,
            },
            [ComponentsListTableColumns.clickCount]: {
              translationKey: 'match_component_click_count',
              sortingEnabled: true,
              headerIcon: {
                template: csqLogoTemplate,
              },
            },
            [ComponentsListTableColumns.clickRate]: {
              translationKey: 'match_component_click_rate',
              sortingEnabled: true,
              headerIcon: {
                template: csqLogoTemplate,
              },
            },
          }
        : {
            [ComponentsListTableColumns.open]: {
              translationKey: 'open',
              sortingEnabled: true,
            },
            [ComponentsListTableColumns.dismissed]: {
              translationKey: 'dismissed',
              sortingEnabled: true,
            },
            [ComponentsListTableColumns.failures]: {
              translationKey: 'tab_failed',
              sortingEnabled: true,
            },
            [ComponentsListTableColumns.reviews]: {
              translationKey: 'label_requires_review',
              sortingEnabled: true,
            },
          }),
    };

    const dsPageMessageConfig: ITableDsPageMessageConfig = {
      icon: Icons.NotFoundBrand,
      iconColor: DsColors.Primary,
      iconScale: IconScale.FA_4X,
      iconStyle: IconStyles.Light,
      variant: DsPageMessageVariants.info,
    };
    const emptyStateTitle: string = this.translateService.instant('no_components_yet');
    const emptyStateSubtitle: string = this.translateService.instant('component_results_empty_state');
    const emptyState: ITableEmptyState = this.useDsPageMessage
      ? {
          type: TableEmptyStateType.dsPageMessage,
          title: emptyStateTitle,
          subtitle: emptyStateSubtitle,
          dsPageMessage: dsPageMessageConfig,
        }
      : {
          iconId: 'file-tray',
          title: emptyStateTitle,
          subtitle: emptyStateSubtitle,
        };

    return {
      columns,
      caption: this.translateService.instant('table_caption_issue_by_components_list'),
      emptyState,
    };
  }

  public onSort(event: SortEvent): void {
    this.sortOption$.next(event);
  }

  public onPageChange(page: number): void {
    this.page$.next(page);
  }

  public onPageSizeChange(pageSize: number): void {
    this.page$.next(1);
    this.pageSize$.next(pageSize);
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.issuesByComponents$.pipe(map((v: IByComponentIssueCount): number => Object.keys(v).length)).subscribe(this.totalRows$),
    );

    this.subscriptions.add(
      combineLatest([this.workspaceId$, this.digitalPropertyId$])
        .pipe(switchMap(this.getComponentData.bind(this)))
        .subscribe(this.components$),
    );

    this.subscriptions.add(
      combineLatest([this.scanSummary$, this.scanAudits$])
        .pipe(map(this.groupIssuesByComponents.bind(this)))
        .subscribe(this.issuesByComponents$),
    );

    this.subscriptions.add(
      combineLatest([this.issuesByComponents$, this.page$, this.pageSize$, this.components$, this.auditTool$, this.sortOption$])
        .pipe(map(this.getTableData.bind(this)))
        .subscribe(this.tableData$),
    );

    this.subscriptions.add(
      this.contentsquareIntegrationService.contentsquareIntegrationEnabled$.subscribe(
        (contentsquareIntegrationEnabled: boolean): void => {
          this.contentsquareIntegrationEnabled = contentsquareIntegrationEnabled;
        },
      ),
    );

    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.dsPageMessage, false)
        .pipe(map(Boolean), startWith(this.useDsPageMessage))
        .subscribe((useDsPageMessage: boolean): void => {
          this.useDsPageMessage = useDsPageMessage;
          this.changeDetectorRef.markForCheck();
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
